'use client';

import { useId, type CSSProperties } from 'react';
import { useTranslations } from 'next-intl';
import CustomLink from 'components/shared/CustomLink';
import { useMarketingBannerOffset } from 'components/shared/Header/MarketingBanner';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import {
  LAB_PRICE_CUT_ENDS,
  LAB_PRICE_CUT_STARTS,
} from 'components/performance-lab/buy/india/priceCut';
import { FESTIVE_CDN_ASSETS } from 'components/performance-lab/festive/festiveAssets';
import { homepageCtaProps } from './v3/homepageCta';
import v3 from './v3/v3.module.css';
import styles from './PlFestiveHero.module.css';

/**
 * India homepage slide for the Lab festive gift (Figma Performance-Lab-Web
 * 6263:1456 desktop, 6279:1814 mobile). Same card as TwoHourDeliveryHero, so
 * the two sit in one carousel. Runs with the Lab price cut.
 */
const OFFER_STARTS = Date.parse(LAB_PRICE_CUT_STARTS);
const OFFER_ENDS = Date.parse(LAB_PRICE_CUT_ENDS);
const GIFT_HREF = '/performance-lab/gift/';

const ARC_PATH =
  'M541.5 20.5C829.207 20.5 1062.5 271.186 1062.5 580.5C1062.5 889.814 829.207 1140.5 541.5 1140.5C253.793 1140.5 20.5 889.814 20.5 580.5C20.5 271.186 253.793 20.5 541.5 20.5Z';

export function isPlFestiveHeroWindow(now: number): boolean {
  return now >= OFFER_STARTS && now < OFFER_ENDS;
}

/**
 * Figma ellipse 6279:1787: a 1px gold stroke under a progressive blur, drawn
 * as a sharp copy fading out over a blurred copy (Figma's 10px) fading in.
 */
const Arc = () => {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const id = (name: string) => `pl-festive-arc-${name}-${uid}`;

  return (
    <svg
      className={styles.arc}
      viewBox="0 0 1083 1161"
      preserveAspectRatio="none"
      fill="none"
      focusable="false"
    >
      <defs>
        <linearGradient
          id={id('stroke')}
          x1="-74.1976"
          y1="-514.856"
          x2="750.344"
          y2="-326.64"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#F3E3BF" />
          <stop offset="0.219843" stopColor="#D7B57A" />
          <stop offset="1" stopColor="#A88148" />
        </linearGradient>
        <linearGradient id={id('fade-out')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0.17" stopColor="#fff" />
          <stop offset="0.28" stopColor="#000" />
        </linearGradient>
        <linearGradient id={id('fade-in')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0.17" stopColor="#000" />
          <stop offset="0.28" stopColor="#fff" />
        </linearGradient>
        <mask
          id={id('sharp')}
          maskUnits="userSpaceOnUse"
          x="0"
          y="0"
          width="1083"
          height="1161"
        >
          <rect width="1083" height="1161" fill={`url(#${id('fade-out')})`} />
        </mask>
        <mask
          id={id('soft')}
          maskUnits="userSpaceOnUse"
          x="0"
          y="0"
          width="1083"
          height="1161"
        >
          <rect width="1083" height="1161" fill={`url(#${id('fade-in')})`} />
        </mask>
        <filter
          id={id('blur')}
          x="0"
          y="0"
          width="1083"
          height="1161"
          filterUnits="userSpaceOnUse"
        >
          <feGaussianBlur stdDeviation="10" />
        </filter>
      </defs>
      <path
        d={ARC_PATH}
        stroke={`url(#${id('stroke')})`}
        mask={`url(#${id('sharp')})`}
      />
      <g mask={`url(#${id('soft')})`}>
        <path
          d={ARC_PATH}
          stroke={`url(#${id('stroke')})`}
          filter={`url(#${id('blur')})`}
        />
      </g>
    </svg>
  );
};

export const PlFestiveHero = ({
  eager = true,
}: {
  /** When false, the photo loads lazily (off-screen carousel slides). */
  eager?: boolean;
}) => {
  const t = useTranslations('homepage');
  const bannerPx = useMarketingBannerOffset();
  const titleId = useId();

  return (
    <section
      className={styles.section}
      aria-labelledby={titleId}
      data-testid="pl-festive-hero"
      data-header-theme="light"
      style={{ '--campaign-banner-px': `${bannerPx}px` } as CSSProperties}
    >
      <div className={styles.art} aria-hidden>
        <Arc />
        {/* Figma layer order: IV Therapy sits behind the photo, the other two in front. */}
        <span className={`${styles.chip} ${styles.chipFaint} ${styles.chipIv}`}>
          {t('homepage.plFestiveHero.chips.ivTherapy')}
        </span>
        <div className={styles.photo}>
          {/* The gift page's IV panel cutout (bottom blur baked in). */}
          <div className={styles.cutout}>
            <CustomImage
              src={FESTIVE_CDN_ASSETS.ivBannerDesk}
              alt=""
              fill
              sizes="(max-width: 899px) 690px, 58vw"
              loading={eager ? 'eager' : 'lazy'}
            />
          </div>
        </div>
        <span className={`${styles.chip} ${styles.chipRecovery}`}>
          {t('homepage.plFestiveHero.chips.recoverySessions')}
        </span>
        <span className={`${styles.chip} ${styles.chipLongevity}`}>
          {t('homepage.plFestiveHero.chips.longevityProtocols')}
        </span>
      </div>
      <div className={styles.copy}>
        <p className={styles.eyebrow}>{t('homepage.plFestiveHero.eyebrow')}</p>
        <h2 id={titleId} className={`${v3.campaignDisplay} ${styles.title}`}>
          {t('homepage.plFestiveHero.titleLine1')}
          <br />
          {t('homepage.plFestiveHero.titleLine2')}
        </h2>
        <p className={styles.description}>
          {t.rich('homepage.plFestiveHero.description', {
            br: () => <br />,
          })}
        </p>
        <CustomLink
          href={GIFT_HREF}
          className={styles.cta}
          {...homepageCtaProps('pl_festive_hero_cta')}
        >
          {t('homepage.plFestiveHero.cta')}
        </CustomLink>
      </div>
    </section>
  );
};
